import { v4 } from 'uuid';
import { Entity } from '../../../../common/ddd/entity.base';
import { AccountSummaryItemAggregateRoot } from './account-summary-item.aggregate-root';
import { AccountSummaryItemEntity } from '../../entities/org-metrics/account-summary-item.entity';

export interface AccountCallsSummaryAggregateRootProps {
  data: AccountSummaryItemAggregateRoot[];
  thisPeriodSummary: AccountSummaryItemEntity[];
  previousPeriodSummary: AccountSummaryItemEntity[];
}

export class AccountCallsSummaryAggregateRoot extends Entity<AccountCallsSummaryAggregateRootProps> {
  protected _id: string;
  protected _hourlyRate: number;
  static create(
    props: AccountCallsSummaryAggregateRootProps,
    entityId?: string
  ): AccountCallsSummaryAggregateRoot {
    const id = entityId || v4();
    const entity = new AccountCallsSummaryAggregateRoot({
      id,
      props,
    });
    return entity;
  }
  public validate(): void {}

  setHourlyRate(hourlyRate: number) {
    this._hourlyRate = hourlyRate;
    this.props.data.forEach((ele) => {
      ele.hourlyRate = hourlyRate;
    });
    this.props.thisPeriodSummary.forEach((ele) => {
      ele.hourlyRate = hourlyRate;
    });
    this.props.previousPeriodSummary.forEach((ele) => {
      ele.hourlyRate = hourlyRate;
    });
  }

  private invalidateCache(): void {
    this._currentPeriodTotals = null;
    this._previousPeriodTotals = null;
  }

  get hourlyRate(): number {
    return this._hourlyRate;
  }

  // Core calculated values - computed once and cached
  private _currentPeriodTotals: { value: number; count: number } | null = null;
  private _previousPeriodTotals: { value: number; count: number } | null = null;

  private getCurrentPeriodTotals(): { value: number; count: number } {
    if (this._currentPeriodTotals === null) {
      let value = 0;
      let count = 0;
      for (const item of this.props.data) {
        value += item.value;
        count += item.count;
      }
      this._currentPeriodTotals = { value, count };
    }
    return this._currentPeriodTotals;
  }

  private getPreviousPeriodTotals(): { value: number; count: number } {
    if (this._previousPeriodTotals === null) {
      let value = 0;
      let count = 0;
      for (const item of this.props.data) {
        value += item.previousPeriodValue;
        count += item.previousPeriodCount;
      }
      this._previousPeriodTotals = { value, count };
    }
    return this._previousPeriodTotals;
  }

  get value(): number {
    return this.thisPeriodSummaryValue;
  }

  get previousPeriodValue(): number {
    return this.previousPeriodSummaryValue;
  }

  get thisPeriodValue(): number {
    return this.thisPeriodSummaryValue;
  }

  get average(): number {
    return this.thisPeriodSummaryCount > 0
      ? this.thisPeriodSummaryValue / this.thisPeriodSummaryCount
      : 0;
  }

  get dataPreviousPeriodValue(): number {
    return this.props.data
      .map((ele) => ele.previousPeriodValue)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get dataPreviousPeriodCount(): number {
    return this.props.data
      .map((ele) => ele.previousPeriodCount)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get dataValue(): number {
    return this.props.data
      .map((ele) => ele.value)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get dataCount(): number {
    return this.props.data
      .map((ele) => ele.count)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get thisPeriodSummaryValue(): number {
    return this.props.thisPeriodSummary
      .map((ele) => ele.value)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get previousPeriodSummaryValue(): number {
    return this.props.previousPeriodSummary
      .map((ele) => ele.value)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get thisPeriodSummaryCount(): number {
    return this.props.thisPeriodSummary
      .map((ele) => ele.count)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get previousPeriodSummaryCount(): number {
    return this.props.previousPeriodSummary
      .map((ele) => ele.count)
      .reduce((acc, ele) => acc + ele, 0);
  }

  get count(): number {
    return this.thisPeriodSummaryCount;
  }

  get previousPeriodCount(): number {
    return this.previousPeriodSummaryCount;
  }

  get previousPeriodAverage(): number {
    return this.previousPeriodSummaryCount > 0
      ? this.previousPeriodSummaryValue / this.previousPeriodSummaryCount
      : 0;
  }

  get cost(): number {
    return this.hourlyRate * this.value;
  }

  get previousPeriodCost(): number {
    return this.hourlyRate * this.previousPeriodValue;
  }

  get averageCost(): number {
    return this.count > 0 ? this.cost / this.count : 0;
  }

  get previousPeriodAverageCost(): number {
    return this.previousPeriodCount > 0
      ? this.previousPeriodCost / this.previousPeriodCount
      : 0;
  }

  get deviation(): number {
    return this.value - this.previousPeriodValue;
  }

  get averageDeviation(): number {
    return this.average - this.previousPeriodAverage;
  }

  get costDeviation(): number {
    return this.cost - this.previousPeriodCost;
  }

  get averageCostDeviation(): number {
    return this.averageCost - this.previousPeriodAverageCost;
  }

  get data(): AccountSummaryItemAggregateRoot[] {
    return this.props.data;
  }
}
